import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  DailyProvider,
  useParticipantIds,
  useLocalParticipant,
  useDaily,
  DailyVideo,
  DailyAudio,
} from '@daily-co/daily-react';
import { Phone, Video, Mic, MicOff, VideoOff, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { usePatientStore } from '../stores/patient';
import { useConsultation, formatDuration } from '../hooks/useConsultation';
import { Button } from '../components/ui/Button';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Inner video call view — rendered inside DailyProvider
function ConsultationView() {
  const navigate = useNavigate();
  const { consultation, patient } = usePatientStore();
  const { duration, endConsultation } = useConsultation();

  const daily = useDaily();
  const localParticipant = useLocalParticipant();
  const participantIds = useParticipantIds({ filter: 'remote' });

  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [doctorJoined, setDoctorJoined] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  // Join the call using useDaily() — same pattern as the working doctor portal
  useEffect(() => {
    if (!daily || !consultation?.roomUrl || !consultation?.token || hasJoined || isJoining) return;

    setIsJoining(true);

    daily
      .join({
        url: consultation.roomUrl,
        token: consultation.token,
      })
      .then(() => {
        setHasJoined(true);
        setIsJoining(false);
        toast.success('Connected to consultation');
      })
      .catch((err) => {
        console.error('Failed to join call:', err);
        setJoinError(err.message || 'Failed to join video call');
        setIsJoining(false);
        toast.error('Failed to connect to video call');
      });
  }, [daily, consultation?.roomUrl, consultation?.token, hasJoined, isJoining]);

  // Track when doctor joins
  useEffect(() => {
    if (participantIds.length > 0 && !doctorJoined) {
      setDoctorJoined(true);
      toast.success('Doctor has joined the call');
    }
  }, [participantIds, doctorJoined]);

  // Handle call events
  useEffect(() => {
    if (!daily) return;

    const handleLeftMeeting = () => {
      toast.info('Call ended');
      navigate('/feedback');
    };

    const handleParticipantLeft = (event: { participant: { user_name?: string; local: boolean } }) => {
      if (!event.participant.local) {
        toast.info('Doctor has left the call');
      }
    };

    const handleError = (event: { errorMsg?: string }) => {
      console.error('Daily error:', event);
      toast.error(event.errorMsg || 'A call error occurred');
    };

    daily.on('left-meeting', handleLeftMeeting);
    daily.on('participant-left', handleParticipantLeft);
    daily.on('error', handleError);

    return () => {
      daily.off('left-meeting', handleLeftMeeting);
      daily.off('participant-left', handleParticipantLeft);
      daily.off('error', handleError);
    };
  }, [daily, navigate]);

  const toggleMute = useCallback(() => {
    if (daily) {
      const newMuted = !isMuted;
      daily.setLocalAudio(!newMuted);
      setIsMuted(newMuted);
      toast.info(newMuted ? 'Microphone muted' : 'Microphone unmuted');
    }
  }, [daily, isMuted]);

  const toggleVideo = useCallback(() => {
    if (daily) {
      const newVideoOff = !isVideoOff;
      daily.setLocalVideo(!newVideoOff);
      setIsVideoOff(newVideoOff);
      toast.info(newVideoOff ? 'Camera turned off' : 'Camera turned on');
    }
  }, [daily, isVideoOff]);

  const handleEndCall = useCallback(async () => {
    setIsEnding(true);
    try {
      await endConsultation();
      if (daily) {
        await daily.leave();
      }
    } catch (error) {
      console.error('Error ending call:', error);
      toast.error('Failed to end call properly');
    }
    navigate('/feedback');
  }, [daily, endConsultation, navigate]);

  // Show join error
  if (joinError) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="size-10 text-red-400" />
          </div>
          <h2 className="text-2xl text-white mb-4">Connection Error</h2>
          <p className="text-slate-400 mb-8">{joinError}</p>
          <Button
            onClick={() => {
              setJoinError(null);
              setIsJoining(false);
              setHasJoined(false);
            }}
            className="px-8 py-4 text-lg bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Show joining state
  if (isJoining || !hasJoined) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="size-16 text-emerald-500 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl text-white mb-2">Joining video call...</h2>
          <p className="text-slate-400">Please allow camera and microphone access</p>
        </div>
      </div>
    );
  }

  const doctorSessionId = participantIds[0];

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col">
      {/* Video Area */}
      <div className="flex-1 relative">
        {/* Doctor's Video (Main) */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900">
          {doctorSessionId ? (
            <DailyVideo
              sessionId={doctorSessionId}
              type="video"
              className="w-full h-full object-cover"
              mirror={false}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="size-16 text-slate-400 animate-spin mx-auto mb-4" />
                <p className="text-xl text-slate-400">Waiting for doctor to join...</p>
              </div>
            </div>
          )}

          {/* Doctor Info Overlay */}
          <div className="absolute top-6 left-6 bg-black/60 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/10">
            <h3 className="text-2xl text-white">
              {consultation?.doctorName || 'Doctor'}
            </h3>
            <p className="text-lg text-slate-300">
              {consultation?.doctorSpecialty || 'General Practitioner'}
            </p>
            {!doctorJoined && (
              <p className="text-sm text-yellow-400 mt-1">Connecting...</p>
            )}
          </div>

          {/* Call Duration */}
          <div className="absolute top-6 right-6 bg-black/60 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/10">
            <p className="text-2xl text-white font-mono">{formatDuration(duration)}</p>
          </div>

          {/* Patient's Video (PiP - Picture in Picture) */}
          <div className="absolute bottom-6 right-6 w-64 h-48 bg-slate-900 rounded-2xl border-4 border-slate-700 overflow-hidden shadow-2xl">
            {localParticipant && !isVideoOff ? (
              <DailyVideo
                sessionId={localParticipant.session_id}
                type="video"
                className="w-full h-full object-cover"
                mirror={true}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
                {isVideoOff ? (
                  <VideoOff className="size-16 text-slate-400" />
                ) : (
                  <div className="text-center">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Video className="size-10 text-white" />
                    </div>
                    <p className="text-white text-sm">{patient?.firstName || 'You'}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audio (hidden) */}
      <DailyAudio />

      {/* Controls */}
      <div className="bg-[#1e293b] border-t border-slate-700 p-6">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-6">
          {/* Mute/Unmute */}
          <Button
            onClick={toggleMute}
            className={`w-20 h-20 rounded-full ${
              isMuted
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-slate-700 hover:bg-slate-600'
            } transition-all shadow-lg`}
          >
            {isMuted ? (
              <MicOff className="size-8 text-white" />
            ) : (
              <Mic className="size-8 text-white" />
            )}
          </Button>

          {/* End Call */}
          <Button
            onClick={handleEndCall}
            disabled={isEnding}
            className="w-24 h-24 rounded-full bg-red-600 hover:bg-red-700 transition-all shadow-2xl disabled:opacity-50"
          >
            {isEnding ? (
              <Loader2 className="size-10 text-white animate-spin" />
            ) : (
              <Phone className="size-10 text-white rotate-[135deg]" />
            )}
          </Button>

          {/* Video On/Off */}
          <Button
            onClick={toggleVideo}
            className={`w-20 h-20 rounded-full ${
              isVideoOff
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-slate-700 hover:bg-slate-600'
            } transition-all shadow-lg`}
          >
            {isVideoOff ? (
              <VideoOff className="size-8 text-white" />
            ) : (
              <Video className="size-8 text-white" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Consultation component
export function Consultation() {
  const navigate = useNavigate();
  const { consultation, queueEntry, patient, setConsultation, updateQueueStatus } = usePatientStore();
  const { room, isLoading, error, fetchRoom } = useConsultation();
  const cancelCheckRef = useRef<number | null>(null);

  // Redirect if no consultation or queue entry
  useEffect(() => {
    if (!consultation && !queueEntry) {
      navigate('/');
    }
  }, [consultation, queueEntry, navigate]);

  // Fetch room details (token) — always call fetchRoom to get a meeting token
  useEffect(() => {
    if (consultation?.id && (!room?.token || !room?.roomUrl)) {
      fetchRoom();
    }
  }, [consultation?.id, room?.token, room?.roomUrl, fetchRoom]);

  // Poll consultation status to detect cancellation (reliable fallback)
  useEffect(() => {
    if (!consultation?.id || !patient?.id) return;

    const checkStatus = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/v1/consultations/${consultation.id}/room?patientId=${patient.id}`
        );
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const code = data?.error?.code;
          // Consultation ended/cancelled/not found — return to queue
          if (code === 'CONSULTATION_ENDED' || code === 'NOT_FOUND' || res.status === 404 || res.status === 400) {
            setConsultation(null);
            updateQueueStatus('waiting');
            toast.info('Your consultation was cancelled. You have been returned to the queue.');
            navigate('/queue');
          }
        }
      } catch {
        // Network error — ignore, will retry
      }
    };

    // Check every 5 seconds
    cancelCheckRef.current = window.setInterval(checkStatus, 5000);

    return () => {
      if (cancelCheckRef.current) {
        clearInterval(cancelCheckRef.current);
      }
    };
  }, [consultation?.id, patient?.id, setConsultation, updateQueueStatus, navigate]);

  // Handle fetch error
  if (error) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="size-10 text-red-400" />
          </div>
          <h2 className="text-2xl text-white mb-4">Connection Error</h2>
          <p className="text-slate-400 mb-8">{error.message}</p>
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => fetchRoom()}
              className="px-8 py-4 text-lg bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="px-8 py-4 text-lg bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700 rounded-xl"
            >
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while fetching room details
  if (isLoading || !room?.roomUrl || !room?.token) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="size-16 text-emerald-500 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl text-white mb-2">Connecting to your consultation...</h2>
          <p className="text-slate-400">Please wait while we set up your video call</p>
        </div>
      </div>
    );
  }

  // Room is ready — render DailyProvider (no external call object, matching doctor pattern)
  return (
    <DailyProvider>
      <ConsultationView />
    </DailyProvider>
  );
}
